/**
 * <collection-filter-buttons>
 *
 * Self-contained, progressively-enhanced single-select filter row for the
 * collection page. Buttons are real links that filter via full page load when
 * JS is unavailable. When enhanced, clicks swap the product grid in place
 * (Section Rendering API), update the active button, and push the clean URL.
 *
 * This page does not load facets.js (filtering is disabled on the grid), so
 * this component owns its own fetch/render logic and depends only on the grid's
 * stable IDs: #product-grid[data-id] and #ProductGridContainer.
 */
if (!customElements.get('collection-filter-buttons')) {
  class CollectionFilterButtons extends HTMLElement {
    constructor() {
      super();
      this.buttons = Array.from(this.querySelectorAll('.fm-filter-buttons__button'));
      this.list = this.querySelector('.fm-filter-buttons__list');
      this.enabled = this.dataset.enableAjax === 'true';

      if (!this.enabled) return;

      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.buttons.forEach((button) => button.addEventListener('click', this.onClick));
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
    }

    get grid() {
      return document.getElementById('product-grid');
    }

    get container() {
      return document.getElementById('ProductGridContainer');
    }

    onClick(event) {
      // If the grid isn't on the page, let the link navigate natively.
      if (!this.grid || !this.container) return;

      const button = event.currentTarget;
      event.preventDefault();

      this.setActive(button);
      this.render(button.href);
      history.pushState({ fmFilterUrl: button.href }, '', button.href);
    }

    onPopState() {
      if (!this.grid || !this.container) return;
      const url = window.location.href;
      this.setActiveByUrl(url);
      this.render(url);
    }

    render(href) {
      const target = new URL(href, window.location.origin);
      const sectionId = this.grid.dataset.id;
      const params = target.search ? `&${target.search.slice(1)}` : '';
      const fetchUrl = `${target.pathname}?section_id=${sectionId}${params}`;

      if (this.list) this.list.classList.add('loading');

      fetch(fetchUrl)
        .then((response) => response.text())
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const newContainer = html.getElementById('ProductGridContainer');
          if (newContainer) this.container.innerHTML = newContainer.innerHTML;
        })
        .catch(() => {
          // Network failure — fall back to a full navigation so the user still filters.
          window.location.href = href;
        })
        .finally(() => {
          if (this.list) this.list.classList.remove('loading');
        });
    }

    setActive(activeButton) {
      this.buttons.forEach((button) => {
        const isActive = button === activeButton;
        button.classList.toggle('is-active', isActive);
        if (isActive) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });
    }

    setActiveByUrl(url) {
      const target = new URL(url, window.location.origin);
      const match = this.buttons.find((button) => {
        const buttonUrl = new URL(button.href, window.location.origin);
        return buttonUrl.pathname + buttonUrl.search === target.pathname + target.search;
      });
      if (match) this.setActive(match);
    }
  }

  customElements.define('collection-filter-buttons', CollectionFilterButtons);
}
